import { getDataStoreEntityMap } from './dataStoreEntityMap'
import { isIncomingOperatorMessage } from './isIncomingOperatorMessage'
import { messageMatchesDeviceThread, messageMatchesUserThread } from './chatThreadMessages'
import { resolveDeviceChatIds } from './resolveDeviceChatIds'

/**
 * @param {Record<string, object>} devicesById
 * @param {string|null|undefined} deviceId
 * @returns {string[]}
 */
export function getThreadDeviceIdCandidates (devicesById, deviceId) {
  if (!deviceId) return []
  const { storeId, uuid } = resolveDeviceChatIds(devicesById, deviceId)
  return [...new Set([String(deviceId), uuid, storeId].filter(Boolean).map(String))]
}

/**
 * @param {object} rootState
 * @param {{ deviceUuid?: string|null, userId?: string|null, profileId?: string }} params
 * @returns {Array<{ id: string, patch: object }>}
 */
export function buildMainChatThreadUnreadClearPatches (rootState, { deviceUuid, userId, profileId }) {
  if (!profileId) return []

  const messagesById = getDataStoreEntityMap(rootState.data.messages)
  const devicesById = getDataStoreEntityMap(rootState.data.devices)
  const patches = []

  for (const [id, message] of Object.entries(messagesById)) {
    if (message.receiverChatGroupId) continue
    if (message.read) continue
    if (!isIncomingOperatorMessage(message, profileId)) continue

    let matches = false
    if (deviceUuid) {
      const candidates = getThreadDeviceIdCandidates(devicesById, deviceUuid)
      for (const candidate of candidates) {
        if (messageMatchesDeviceThread(message, candidate, profileId)) {
          matches = true
          break
        }
      }
    } else if (userId) {
      matches = messageMatchesUserThread(message, userId, profileId)
    }

    if (matches) {
      patches.push({ id, patch: { read: true, received: true } })
    }
  }

  return patches
}

/**
 * All unread device/user main-chat inbox rows (not a single thread).
 * @param {object} rootState
 * @param {string} profileId
 * @returns {Array<{ id: string, patch: object }>}
 */
export function buildAllMainChatInboxUnreadClearPatches (rootState, profileId) {
  if (!profileId) return []

  const messagesById = getDataStoreEntityMap(rootState.data.messages)
  const patches = []

  for (const [id, message] of Object.entries(messagesById)) {
    if (message.receiverChatGroupId) continue
    if (message.read) continue
    if (!isIncomingOperatorMessage(message, profileId)) continue
    patches.push({ id, patch: { read: true, received: true } })
  }

  return patches
}

/**
 * Device thread uuids that still have unread inbox messages (for messages::read per device).
 * @param {object} rootState
 * @param {string} profileId
 * @returns {string[]}
 */
export function collectUnreadMainChatDeviceUuids (rootState, profileId) {
  if (!profileId) return []

  const messagesById = getDataStoreEntityMap(rootState.data.messages)
  const uuids = new Set()

  for (const message of Object.values(messagesById)) {
    if (message.receiverChatGroupId || message.read) continue
    if (!isIncomingOperatorMessage(message, profileId)) continue
    const deviceUuid = message.senderDeviceId || message.receiverDeviceId
    if (
      deviceUuid &&
      deviceUuid !== 'operator' &&
      deviceUuid !== 'system' &&
      String(deviceUuid).toLowerCase() !== 'operator'
    ) {
      uuids.add(String(deviceUuid))
    }
  }

  return [...uuids]
}
